import { getMessage, i18n } from '@gef-ui/localization/messages';
import UploadFileDisabled from '@gef-ui/material-illustrations-2023/status/FileUploadDisabledFidelityHigh';
import UploadFile from '@gef-ui/material-illustrations-2023/status/FileUploadPositiveFidelityHigh';
import byteToMBRounded from "@gef-ui/utils/byteToMBRounded";
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Chip, { chipClasses } from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useControlled } from '@mui/material/utils';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans } from 'react-i18next';
import useIsTabletAndUp from '../../hooks/useIsTabletAndUp';
import { addToFiles, rejectAndRevoke } from '../FileInput/utils';
import commonMimeTypes from '../commonMimeTypes';
import mimeToIcon from '../mimeToIcon';
import { passSx, sxPropType, useDefaultComponentValue } from '../utils';
const BG_COLOR = {
    error: 'error.shades.4p',
    success: 'success.shades.4p'
};
const BORDER_COLOR = {
    normal: 'primary.shades.30p',
    success: 'success.shades.30p',
    error: 'error.shades.30p',
    disabled: 'action.disabled'
};
export const transformExtensions = (acceptExtensions)=>{
    // NOTE: this crazy function is mainly because of this shit
    // https://github.com/react-dropzone/react-dropzone/issues/1220
    const parsed = acceptExtensions.split(',');
    const fileExts = parsed.filter((ext)=>ext.startsWith('.'));
    const mimes = parsed.filter((ext)=>ext.includes('/')).map((mime)=>[
            mime,
            []
        ]);
    const baseMimes = Object.fromEntries(mimes);
    if (fileExts.length > 0) {
        const rawResult = {};
        fileExts.forEach((ext)=>{
            Object.entries(commonMimeTypes).forEach(([key, value])=>{
                if (value.includes(ext.toLowerCase())) {
                    rawResult[key] = [
                        ext,
                        ...rawResult[key] || []
                    ];
                }
            });
        });
        Object.keys(baseMimes).forEach((key)=>{
            if (key.includes('*')) {
                const baseMime = key.split('/').shift();
                Object.keys(rawResult).forEach((key)=>{
                    if (key.includes(baseMime)) {
                        delete rawResult[key];
                    }
                });
            }
        });
        return {
            ...baseMimes,
            ...rawResult
        };
    } else {
        return baseMimes;
    }
};
const extensionUpper = (ext)=>ext.replace('.', '').toUpperCase();
export const extensionsToLabel = (accept)=>{
    let result = [];
    Object.entries(accept).forEach(([key, value])=>{
        if (key.includes('*')) {
            result = [
                getMessage(`common:mime-${key.replace('/*', '')}`),
                ...result
            ];
        } else if (value.length > 0) {
            result = [
                ...result,
                ...value.map(extensionUpper)
            ];
        } else {
            result = [
                ...result,
                ...commonMimeTypes[key].map(extensionUpper)
            ];
        }
    });
    return result;
};
export const COMPONENT_NAME = 'Dropzone';
const Dropzone = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { id, acceptExtensions, acceptSize, totalAcceptSize, showSize, acceptAll, disabled = false, append = false, hideValidationText, validationText, handleFileChange, errMeta = [], value: valueProp, hideIllustration, Illustration = /*#__PURE__*/ React.createElement(UploadFile, {
        style: {
            flexShrink: 0
        },
        width: 98,
        height: 98
    }), SuccessIllustration = /*#__PURE__*/ React.createElement(UploadFile, {
        style: {
            flexShrink: 0
        },
        width: 98,
        height: 98
    }), DisabledIllustration = /*#__PURE__*/ React.createElement(UploadFileDisabled, {
        style: {
            flexShrink: 0
        },
        width: 98,
        height: 98
    }), DropzoneProps = {}, PaperProps = {}, numOfFiles, onChipClick, sx } = props;
    const isTabletAndUp = useIsTabletAndUp();
    const [value, setValue] = useControlled({
        controlled: valueProp,
        default: [],
        name: 'FileInput'
    });
    const onDropAccepted = useCallback((acceptedFiles, e)=>{
        const newFiles = addToFiles(value, append, acceptedFiles);
        setValue(newFiles);
        handleFileChange?.(e, newFiles);
    }, [
        append,
        handleFileChange,
        setValue,
        value
    ]);
    const handleRemoveFile = useCallback((name, e)=>{
        const newFiles = rejectAndRevoke(name, value);
        setValue(newFiles);
        handleFileChange?.(e, newFiles);
    }, [
        value,
        handleFileChange,
        setValue
    ]);
    const [accept, acceptLabel] = useMemo(()=>{
        const accept = transformExtensions(acceptExtensions);
        return [
            accept,
            extensionsToLabel(accept)
        ];
    }, [
        acceptExtensions
    ]);
    const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
        noClick: true,
        noKeyboard: true,
        ...DropzoneProps,
        disabled,
        onDropAccepted,
        accept: acceptAll ? undefined : accept,
        // NOTE: allow everything, we can validate it in form
        maxSize: Infinity,
        minSize: 0,
        maxFiles: 0,
        validator: null
    });
    let illustration = Illustration;
    let borderColor = BORDER_COLOR.normal;
    let backgroundColor;
    if (disabled) {
        borderColor = BORDER_COLOR.disabled;
        illustration = DisabledIllustration;
    } else if (errMeta.length === 0 && !validationText && value.length > 0) {
        borderColor = BORDER_COLOR.success;
        backgroundColor = BG_COLOR.success;
        illustration = SuccessIllustration;
    } else if (errMeta.length > 0 || validationText) {
        borderColor = BORDER_COLOR.error;
        backgroundColor = BG_COLOR.error;
    }
    const combinedError = errMeta.reduce((acc, err)=>[
            ...acc,
            err.errorMessage
        ], [
        validationText
    ])// NOTE: emptyError is a workaround for our validation system. We need to have some message defined but we are using only file level validations
    .filter((err)=>err && err !== 'emptyError');
    return /*#__PURE__*/ React.createElement(Paper, {
        ...getRootProps(),
        elevation: 1,
        variant: "dashed",
        radius: 1.5,
        borderColor: borderColor,
        borderDasharray: isDragActive ? 1 : 7,
        sx: [
            {
                position: 'relative',
                backgroundColor
            },
            ...passSx(sx)
        ],
        id: `${id}-root`,
        ...PaperProps
    }, isDragReject && /*#__PURE__*/ React.createElement(Paper, {
        elevation: 0,
        sx: {
            zIndex: 1000,
            position: 'absolute',
            top: '5px',
            right: '5px',
            bottom: '5px',
            left: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        radius: 1.5,
        id: `${id}-rejected`
    }, /*#__PURE__*/ React.createElement(Typography, {
        variant: "h4",
        sx: {
            fontWeight: 'light',
            color: 'error.dark'
        }
    }, /*#__PURE__*/ React.createElement(Trans, {
        i18n: i18n,
        i18nKey: "common:wrongFormat"
    }))), /*#__PURE__*/ React.createElement(Stack, {
        sx: {
            flexDirection: {
                md: 'row',
                xs: 'column'
            },
            gap: {
                md: 3,
                xs: 0
            },
            alignItems: {
                md: 'flex-start',
                xs: 'center'
            },
            py: 2,
            px: 4.25
        }
    }, /*#__PURE__*/ React.createElement("input", {
        ...getInputProps(),
        id: `${id}-input`
    }), !hideIllustration && illustration, /*#__PURE__*/ React.createElement(Stack, {
        sx: {
            flexDirection: 'column',
            alignItems: {
                md: 'flex-start',
                xs: 'center'
            },
            gap: 1,
            pt: {
                md: hideIllustration ? undefined : 2,
                xs: 0
            }
        }
    }, /*#__PURE__*/ React.createElement(Stack, {
        sx: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1
        }
    }, isTabletAndUp && /*#__PURE__*/ React.createElement(Typography, {
        variant: "h4",
        sx: {
            fontWeight: 'light'
        }
    }, /*#__PURE__*/ React.createElement(Trans, {
        i18n: i18n,
        i18nKey: "common:dragFile"
    })), /*#__PURE__*/ React.createElement(Link, {
        component: "a",
        variant: "h4",
        underline: "always",
        onClick: disabled ? (e)=>e.preventDefault() : open,
        id: `${id}-link`,
        disabled: disabled,
        sx: {
            fontWeight: 'light'
        }
    }, /*#__PURE__*/ React.createElement(Trans, {
        i18n: i18n,
        i18nKey: isTabletAndUp ? 'common:choseFileDesktop' : 'common:choseFileMobile'
    }))), /*#__PURE__*/ React.createElement(Typography, {
        variant: "subtitle2",
        sx: {
            textAlign: {
                md: 'left',
                xs: 'center'
            },
            color: 'text.secondary',
            fontWeight: 'regular'
        }
    }, /*#__PURE__*/ React.createElement(Trans, {
        i18n: i18n,
        i18nKey: "common:infoText",
        values: {
            extensions: acceptLabel,
            size: byteToMBRounded(acceptSize)
        }
    }), totalAcceptSize && /*#__PURE__*/ React.createElement(Trans, {
        i18n: i18n,
        i18nKey: "common:totalAcceptSize",
        values: {
            size: byteToMBRounded(totalAcceptSize)
        }
    }), numOfFiles && /*#__PURE__*/ React.createElement(Trans, {
        i18n: i18n,
        i18nKey: "common:numOfFiles",
        values: {
            numOfFiles
        }
    })), value?.length > 0 && /*#__PURE__*/ React.createElement(Stack, {
        sx: {
            alignItems: {
                md: 'flex-start',
                xs: 'center'
            },
            gap: 1
        }
    }, /*#__PURE__*/ React.createElement(Stack, {
        sx: {
            flexDirection: {
                md: 'row',
                xs: 'column'
            },
            alignItems: {
                md: 'flex-start',
                xs: 'center'
            },
            flexWrap: 'wrap',
            gap: 1
        }
    }, value.map((item, index)=>{
        const { blob, name, type } = item;
        const MimeIcon = mimeToIcon(type);
        const hasError = errMeta.some((err)=>err.index === index);
        const label = showSize ? /*#__PURE__*/ React.createElement(Trans, {
            i18n: i18n,
            i18nKey: "common:chipWithSize",
            values: {
                size: byteToMBRounded(item.size),
                name
            }
        }) : name;
        return /*#__PURE__*/ React.createElement(Chip, {
            sx: disabled && onChipClick ? {
                [`&.${chipClasses.disabled}`]: {
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }
            } : undefined,
            disabled: disabled,
            key: blob,
            avatar: /*#__PURE__*/ React.createElement(Avatar, null, /*#__PURE__*/ React.createElement(MimeIcon, null)),
            label: label,
            color: hasError ? 'error' : 'success',
            onDelete: !disabled ? (e)=>handleRemoveFile(name, e) : undefined,
            id: `${id}-chip-${name}`,
            onClick: onChipClick ? (e)=>{
                onChipClick(e, item);
            } : undefined
        });
    })), !hideValidationText && combinedError.length > 0 && /*#__PURE__*/ React.createElement(Alert, {
        sx: {
            width: 1
        },
        severity: "error"
    }, combinedError.map((err)=>/*#__PURE__*/ React.createElement("p", {
            key: err
        }, /*#__PURE__*/ React.createElement(Trans, {
            i18n: i18n,
            defaults: err
        }))))))));
};
Dropzone.propTypes = {
    DisabledIllustration: PropTypes.node,
    DropzoneProps: PropTypes.object,
    Illustration: PropTypes.node,
    PaperProps: PropTypes.object,
    SuccessIllustration: PropTypes.node,
    /**
	 * Accept every file extensions
	 */ acceptAll: PropTypes.bool,
    /**
	 * html accept attribute, see https://www.w3schools.com/TAGs/att_input_accept.asp
	 */ acceptExtensions: PropTypes.string,
    acceptSize: PropTypes.number,
    append: PropTypes.bool,
    disabled: PropTypes.bool,
    errMeta: PropTypes.arrayOf(PropTypes.shape({
        errorMessage: PropTypes.string,
        index: PropTypes.number
    })),
    /**
	 * Callback function fired when file is selected
	 *
	 * @param {object} event event targeting the element.
	 * @param {File} file object. https://developer.mozilla.org/cs/docs/Web/API/File
	 * @return {undefined} Nothin'
	 */ handleFileChange: PropTypes.func,
    hideIllustration: PropTypes.bool,
    hideValidationText: PropTypes.bool,
    id: PropTypes.string,
    numOfFiles: PropTypes.number,
    onChipClick: PropTypes.func,
    showSize: PropTypes.bool,
    sx: sxPropType,
    totalAcceptSize: PropTypes.number,
    validationText: PropTypes.node,
    value: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        size: PropTypes.number,
        blob: PropTypes.string,
        error: PropTypes.string
    }))
};
export default Dropzone;
